export type LatLngTuple = [number, number];

export function isValidLatLng(lat: unknown, lng: unknown): lat is number {
  if (typeof lat !== "number" || typeof lng !== "number") {
    return false;
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

/** Odoo often stores unset floats as 0 — treat that as missing coordinates. */
export function isUnsetMapCoordinate(lat: number, lng: number): boolean {
  return lat === 0 && lng === 0;
}

export function toLatLngTuple(lat: unknown, lng: unknown): LatLngTuple | null {
  if (typeof lat !== "number" || typeof lng !== "number") {
    return null;
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  if (isUnsetMapCoordinate(lat, lng)) {
    return null;
  }
  return [lat, lng];
}

type PolylinePoint = {
  lat?: unknown;
  lng?: unknown;
  latitude?: unknown;
  longitude?: unknown;
};

/** Normalize API polyline payloads to valid [lat, lng] pairs. */
export function normalizePolylinePoints(raw: unknown): LatLngTuple[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const points: LatLngTuple[] = [];
  for (const entry of raw) {
    if (Array.isArray(entry) && entry.length >= 2) {
      const tuple = toLatLngTuple(entry[0], entry[1]);
      if (tuple) {
        points.push(tuple);
      }
      continue;
    }

    if (!entry || typeof entry !== "object") {
      continue;
    }

    const point = entry as PolylinePoint;
    const tuple =
      toLatLngTuple(point.lat, point.lng) ??
      toLatLngTuple(point.latitude, point.longitude);
    if (tuple) {
      points.push(tuple);
    }
  }

  return points;
}

/** Reduce very dense polylines for smoother Leaflet rendering. */
export function simplifyPolylinePoints(
  points: LatLngTuple[],
  maxPoints = 1500
): LatLngTuple[] {
  if (points.length <= maxPoints) {
    return points;
  }

  const step = Math.ceil(points.length / maxPoints);
  const simplified: LatLngTuple[] = [];
  for (let index = 0; index < points.length; index += step) {
    simplified.push(points[index]);
  }

  const last = points[points.length - 1];
  const tail = simplified[simplified.length - 1];
  if (!tail || tail[0] !== last[0] || tail[1] !== last[1]) {
    simplified.push(last);
  }

  return simplified;
}

type LatLngBoundsLike = {
  extend: (latlng: LatLngTuple) => unknown;
  isValid: () => boolean;
};

export function extendBoundsWithPoints(
  bounds: LatLngBoundsLike,
  points: LatLngTuple[]
): void {
  for (const point of points) {
    bounds.extend(point);
  }
}

export type MapViewportMap = {
  fitBounds: (
    bounds: LatLngBoundsLike,
    options?: { padding?: number[]; maxZoom?: number }
  ) => void;
  setView: (coords: LatLngTuple, zoom: number) => void;
};

const DEFAULT_MAP_CENTER: LatLngTuple = [40.7128, -74.006];
const DEFAULT_MAP_ZOOM = 5;
const SINGLE_POINT_MAP_ZOOM = 14;
const MAP_BOUNDS_MAX_ZOOM = 15;

export function collectRouteMapPoints(
  mapData: {
    depot?: { lat?: unknown; lng?: unknown };
    stops?: Array<{ lat?: unknown; lng?: unknown }>;
    polyline?: unknown;
  }
): LatLngTuple[] {
  const points: LatLngTuple[] = [];
  const seen = new Set<string>();

  const addPoint = (point: LatLngTuple | null) => {
    if (!point) {
      return;
    }
    const key = `${point[0].toFixed(6)}:${point[1].toFixed(6)}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    points.push(point);
  };

  addPoint(toLatLngTuple(mapData.depot?.lat, mapData.depot?.lng));

  for (const stop of mapData.stops ?? []) {
    addPoint(toLatLngTuple(stop.lat, stop.lng));
  }

  for (const point of normalizePolylinePoints(mapData.polyline)) {
    addPoint(point);
  }

  return points;
}

export function applyRouteMapViewport(
  map: MapViewportMap,
  boundsFactory: (corner1: LatLngTuple, corner2: LatLngTuple) => LatLngBoundsLike,
  points: LatLngTuple[]
): { infoMessage: string | null } {
  if (points.length === 0) {
    map.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
    return { infoMessage: "No map data (addresses not geocoded)." };
  }

  if (points.length === 1) {
    map.setView(points[0], SINGLE_POINT_MAP_ZOOM);
    return { infoMessage: null };
  }

  let mapBounds = boundsFactory(points[0], points[0]);
  extendBoundsWithPoints(mapBounds, points.slice(1));

  if (mapBounds.isValid()) {
    map.fitBounds(mapBounds, {
      padding: [36, 36],
      maxZoom: MAP_BOUNDS_MAX_ZOOM,
    });
    return { infoMessage: null };
  }

  map.setView(points[0], SINGLE_POINT_MAP_ZOOM);
  return { infoMessage: null };
}

export function safeRemoveMap(map: { remove: () => void } | null | undefined): void {
  if (!map) {
    return;
  }
  try {
    map.remove();
  } catch {
    // Leaflet can throw if the map was already torn down.
  }
}
